import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiStar, FiSave, FiCalendar, FiClock } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import {
  getEventDonations,
  getDerasarPersonUsers,
  registerSpecialDonation,
} from '../../services/api';
import { toDerasarPersonOptions } from '../../utils/derasarPersonOptions';
import OfferingsMemberSelect from '../Offerings/OfferingsMemberSelect';
import styles from './BlindOffer.module.css';
import rStyles from '../Receipts/NewReceiptsPage.module.css';

// -- Date helpers --------------------------------------------------------------
const toLocalDate = (d) => {
  if (!d) return null;
  const s = typeof d === 'string' ? d : d.toISOString();
  const [y, m, day] = s.substring(0, 10).split('-').map(Number);
  return new Date(y, m - 1, day);
};
const fmtDisplay = (d) =>
  toLocalDate(d)?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) ?? '';
const toYMD = (d) => (d ? (typeof d === 'string' ? d : d.toISOString()).substring(0, 10) : '');
const localToday = () => new Date().toLocaleDateString('en-CA');

// -- Page ----------------------------------------------------------------------
const BlindOfferPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeDerasarId } = useAuth();

  const eventFromState = location.state?.event || null;

  const [specialDonations, setSpecialDonations] = useState([]);
  const [memberOptions,    setMemberOptions]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  // Form state
  const [selectedPersonId,   setSelectedPersonId]   = useState('');
  const [selectedPersonMeta, setSelectedPersonMeta] = useState(null);
  const [selectedEdId,       setSelectedEdId]       = useState('');
  const [quantity,           setQuantity]           = useState(1);
  const [regDate,            setRegDate]            = useState(localToday());
  const [notes,              setNotes]              = useState('');

  const minDate = eventFromState ? toYMD(eventFromState.eventDate) : '';
  const maxDate = eventFromState
    ? toYMD(eventFromState.endDate || eventFromState.contributionEndDate)
    : '';

  // Load event donations + member list on mount
  useEffect(() => {
    if (!activeDerasarId) return;
    setLoading(true);
    Promise.all([
      getEventDonations(eventId, activeDerasarId),
      getDerasarPersonUsers(activeDerasarId),
    ])
      .then(([edRes, usersRes]) => {
        const allEd = Array.isArray(edRes.data) ? edRes.data : [];
        const special = allEd.filter((ed) => ed.isSpecial && !ed.settledAt);
        setSpecialDonations(special);
        if (special.length === 1) setSelectedEdId(String(special[0].id));
        setMemberOptions(toDerasarPersonOptions(Array.isArray(usersRes.data) ? usersRes.data : []));
      })
      .catch(() => toast.error('Failed to load blind offer data'))
      .finally(() => setLoading(false));
  }, [eventId, activeDerasarId]);

  useEffect(() => {
    if (!minDate && !maxDate) return;
    setRegDate((prev) => {
      if (minDate && prev < minDate) return minDate;
      if (maxDate && prev > maxDate) return maxDate;
      return prev;
    });
  }, [minDate, maxDate]);

  const handleMemberSelect = (person) => {
    setSelectedPersonId(person.id);
    setSelectedPersonMeta(person);
  };

  const selectedEd = specialDonations.find((d) => String(d.id) === String(selectedEdId));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPersonId) return toast.error('Please select a member');
    if (!selectedEdId)     return toast.error('Please select a blind offer category');
    if (quantity < 1)      return toast.error('Quantity must be at least 1');
    if (selectedEd?.isFull) return toast.error('This blind offer category is full');

    setSaving(true);
    try {
      await registerSpecialDonation(selectedEdId, {
        personId: selectedPersonId,
        quantity,
        registrationDate: regDate || null,
        notes,
      });
      toast.success('Registered for blind offer');
      navigate('/derasar/events');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setSaving(false);
    }
  };

  const ev = eventFromState;

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div className={styles.page}>

      {/* ── Header ── */}
      <div className="page-header">
        <h2><FiStar /> Blind Offer</h2>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/derasar/events')}>
          <FiArrowLeft /> Back to Events
        </button>
      </div>

      {/* ── Scrollable body ── */}
      <div className={styles.scrollArea}>

        {/* Event strip */}
        {ev && (
          <div className={styles.eventStrip}>
            <span className={styles.eventTitle}>{ev.title}</span>
            <span className={styles.eventMeta}>
              <FiCalendar size={13} />
              {fmtDisplay(ev.eventDate)}{ev.endDate ? ` – ${fmtDisplay(ev.endDate)}` : ''}
            </span>
            <span className={styles.eventMeta}>
              <FiClock size={13} /> Open until {fmtDisplay(ev.contributionEndDate)}
            </span>
          </div>
        )}

        {specialDonations.length === 0 ? (
          <div className={styles.emptyState}>
            <FiStar size={40} />
            <p>No blind offer categories are configured for this event.</p>
          </div>
        ) : (
          <form id="blind-offer-form" onSubmit={handleSubmit}>
            <div className="card">

              {/* ── Section 1: Category + Quantity ── */}
              <div className={rStyles.section}>
                <div className={rStyles.sectionLabel}>Blind Offer Category</div>
                <div className={rStyles.row2}>

                  <div className="form-group">
                    <label>Category *</label>
                    {specialDonations.length === 1 ? (
                      <div className={styles.singleCategory}>
                        <FiStar size={14} />
                        <span>{specialDonations[0].category?.name}</span>
                        {specialDonations[0].isFull && (
                          <span className={styles.fullBadge}>Full</span>
                        )}
                      </div>
                    ) : (
                      <select
                        value={selectedEdId}
                        onChange={(e) => setSelectedEdId(e.target.value)}
                        required
                      >
                        <option value="">Select blind offer category…</option>
                        {specialDonations.map((ed) => (
                          <option key={ed.id} value={ed.id} disabled={ed.isFull}>
                            {ed.category?.name}
                            {ed.isFull ? ' (Full)' : ''}
                            {ed.maxCount
                              ? ` — ${ed.registrations?.length || 0}/${ed.maxCount} registered`
                              : ''}
                          </option>
                        ))}
                      </select>
                    )}
                    {selectedEd && (
                      <p className={styles.regHint}>
                        <FiStar size={12} />
                        {selectedEd.totalQuantity ?? (selectedEd.registrations || []).reduce((s, r) => s + (r.quantity || 1), 0)} slot{((selectedEd.totalQuantity ?? 0) !== 1) ? 's' : ''} registered so far
                        {selectedEd.maxCount ? ` · max ${selectedEd.maxCount}` : ''}
                        . Amount will be divided equally per slot after the event.
                      </p>
                    )}
                  </div>

                  <div className="form-group">
                    <label>No. of Blind Offers *</label>
                    <div className={styles.qtyStepperWrap}>
                      <button
                        type="button"
                        className={styles.qtyStepBtn}
                        onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                        disabled={quantity <= 1}
                      >−</button>
                      <input
                        type="number"
                        min={1}
                        max={
                          selectedEd?.maxCount
                            ? selectedEd.maxCount - (selectedEd.registrations?.length || 0)
                            : undefined
                        }
                        step={1}
                        value={quantity}
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10);
                          if (!isNaN(val) && val >= 1) setQuantity(val);
                          else if (e.target.value === '') setQuantity('');
                        }}
                        onBlur={() => setQuantity((q) => (!q || q < 1 ? 1 : Number(q)))}
                        className={styles.qtyStepInput}
                        required
                      />
                      <button
                        type="button"
                        className={styles.qtyStepBtn}
                        onClick={() => setQuantity((q) => Number(q || 0) + 1)}
                      >+</button>
                    </div>
                    {quantity > 1 && (
                      <span className={styles.qtyHint}>
                        Registering for {quantity} offer slots
                      </span>
                    )}
                  </div>

                </div>
              </div>

              <div className={rStyles.divider} />

              {/* ── Section 2: Member ── */}
              <div className={rStyles.section}>
                <div className={rStyles.sectionLabel}>Member</div>
                <div className={rStyles.row2}>

                  <div className="form-group">
                    <label>For Whom *</label>
                    <OfferingsMemberSelect
                      value={selectedPersonId}
                      displayValue={selectedPersonMeta?.name || ''}
                      displayPhone={selectedPersonMeta?.phone || ''}
                      displayEmail={selectedPersonMeta?.email || ''}
                      options={memberOptions}
                      onSelect={handleMemberSelect}
                    />
                  </div>

                </div>
              </div>

              <div className={rStyles.divider} />

              {/* ── Section 3: Date + Notes ── */}
              <div className={rStyles.section}>
                <div className={rStyles.sectionLabel}>Details</div>
                <div className={rStyles.row2}>

                  <div className="form-group">
                    <label>Registration Date *</label>
                    <input
                      type="date"
                      required
                      value={regDate}
                      min={minDate || undefined}
                      max={maxDate || undefined}
                      onChange={(e) => setRegDate(e.target.value)}
                    />
                    {(minDate || maxDate) && (
                      <span className={styles.qtyHint}>
                        {minDate && maxDate
                          ? `${fmtDisplay(minDate)} – ${fmtDisplay(maxDate)}`
                          : minDate
                          ? `From ${fmtDisplay(minDate)}`
                          : `Until ${fmtDisplay(maxDate)}`}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Notes</label>
                    <input
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Optional notes…"
                    />
                  </div>

                </div>
              </div>

            </div>
          </form>
        )}

      </div>{/* end scrollArea */}

      {/* ── Pinned footer ── */}
      {specialDonations.length > 0 && (
        <div className={styles.footer}>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => navigate('/derasar/events')}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="blind-offer-form"
            className="btn btn-primary"
            disabled={saving || !selectedPersonId || !selectedEdId}
          >
            {saving ? 'Registering…' : <><FiSave /> Register Blind Offer</>}
          </button>
        </div>
      )}

    </div>
  );
};

export default BlindOfferPage;
